import axios from "axios";
import { isTokenExpired } from "../../utils/tokenUtils";
import APIs from "../../Constants/apis";
import {
  readCustomerAuthStorage,
  writeCustomerAuthStorage,
  clearCustomerAuthStorage as clearCustomerAuthFromStorage,
  getCustomerAuthStorageKey,
} from "../../utils/customerAuthStorage";
 
let API_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 60000, // Increased to 60 seconds for slower reservation/slot endpoints
  validateStatus: function (status) {
    return status < 500; // Accept any status code less than 500
  }
});

// Public online-ordering client: no auth cookies / CSRF coupling.
export const axiosPublicInstance = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  timeout: 60000,
  validateStatus: function (status) {
    return status < 500;
  }
});
  
const mutatingMethods = new Set(["post", "put", "patch", "delete"]);

const getCookieValue = (name) => {
  const cookieValue = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  return cookieValue ? decodeURIComponent(cookieValue.split("=")[1]) : null;
};

const getRestaurantSlugFromRequestUrl = (url = "") => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(/\/restaurant\/([^/]+)/i);
  if (match?.[1]) return decodeURIComponent(match[1]);
  return null;
};

const getRestaurantSlugFromPathname = () => {
  try {
    const path = window?.location?.pathname || "";
    const match = path.match(/^\/([^/]+)\//);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const readCustomerAuth = (restaurantSlug) => ({
  data: readCustomerAuthStorage(restaurantSlug),
  scopedKey: getCustomerAuthStorageKey(restaurantSlug),
});

const persistCustomerAuthTokens = (restaurantSlug, { token, refreshToken, customer }) => {
  const { data } = readCustomerAuth(restaurantSlug);
  const next = writeCustomerAuthStorage(restaurantSlug, {
    customerToken: token || data?.customerToken || null,
    customerRefreshToken: refreshToken || data?.customerRefreshToken || data?.refreshToken || null,
    customerUser: customer || data?.customerUser || null,
  });
  window.dispatchEvent(
    new CustomEvent("customerTokenRefreshed", {
      detail: { ...next, restaurantSlug },
    })
  );
  return next;
};

const clearCustomerAuthStorage = (restaurantSlug) => {
  clearCustomerAuthFromStorage(restaurantSlug);
};

const forceCustomerLogout = (restaurantSlug) => {
  clearCustomerAuthStorage(restaurantSlug);
  window.dispatchEvent(new CustomEvent("forceCustomerLogout"));
};

/** Checkout/submit can continue as guest if refresh fails. Account APIs cannot. */
const isGuestAllowedCustomerEndpoint = (url = "") =>
  /\/restaurant\/[^/]+\/prepare-checkout/.test(url) ||
  /\/restaurant\/[^/]+\/order(\?|$)/.test(url);

let customerRefreshPromise = null;

export const tryRefreshCustomerToken = async (restaurantSlug) => {
  if (!restaurantSlug) return null;
  if (!customerRefreshPromise) {
    customerRefreshPromise = (async () => {
      const { data } = readCustomerAuth(restaurantSlug);
      const presented =
        data?.customerRefreshToken ||
        data?.refreshToken ||
        data?.customerToken ||
        data?.token ||
        "";
      if (!presented) return null;

      const response = await axiosPublicInstance.post(
        APIs.REFRESH_CUSTOMER_TOKEN(restaurantSlug),
        { refreshToken: presented }
      );
      const payload = response.data?.data ?? response.data?.Data;
      const ok = response.data?.status ?? response.data?.Status;
      const token = payload?.token ?? payload?.Token;
      if (response.status >= 400 || !ok || !token) {
        return null;
      }
      return persistCustomerAuthTokens(restaurantSlug, {
        token,
        refreshToken: payload?.refreshToken ?? payload?.RefreshToken,
        customer: payload?.customer ?? payload?.Customer,
      });
    })().finally(() => {
      customerRefreshPromise = null;
    });
  }
  try {
    return await customerRefreshPromise;
  } catch {
    return null;
  }
};

/** Online-ordering routes that require the customer JWT (not public menu/checkout). */
const isOnlineOrderingCustomerEndpoint = (url = "") =>
  !!url &&
  (url.includes("/my-orders") ||
    /\/restaurant\/[^/]+\/order\/[^/]+/.test(url) ||
    /\/restaurant\/[^/]+\/order(\?|$)/.test(url) ||
    /\/restaurant\/[^/]+\/prepare-checkout/.test(url) ||
    /\/restaurant\/[^/]+\/customer\/addresses/.test(url) ||
    /\/restaurant\/[^/]+\/customer\/notifications/.test(url) ||
    /\/restaurant\/[^/]+\/customer\/loyalty/.test(url) ||
    /\/restaurant\/[^/]+\/rewards/.test(url));

// Request interceptor to add auth token and handle online ordering timeouts
axiosInstance.interceptors.request.use(async (config) => {
  if (mutatingMethods.has((config.method || "get").toLowerCase())) {
    const csrfToken = getCookieValue("csrf_token");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }

  const isPublicOnlineReservationEndpoint =
    !!config.url && config.url.includes('/api/online-reservation/');
  const isOnlineOrderingEndpoint =
    !!config.url && config.url.includes('/api/online-ordering/');

  // Keep longer timeout for public online ordering/reservation endpoints (can be slower)
  if (
    config.url &&
    (config.url.includes('/api/online-ordering/') ||
      config.url.includes('/api/online-reservation/'))
  ) {
    config.timeout = Math.max(config.timeout || 0, 60000); // 60 seconds
  }

  const isCustomerEndpoint = isOnlineOrderingCustomerEndpoint(config.url);

  // Public online-ordering/reservation endpoints should not carry BackOffice/customer auth context.
  // This keeps storefront calls isolated from stale BackOffice cookies/CSRF requirements.
  if ((isPublicOnlineReservationEndpoint || isOnlineOrderingEndpoint) && !isCustomerEndpoint) {
    if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }
    if (config.headers?.["X-CSRF-Token"]) {
      delete config.headers["X-CSRF-Token"];
    }
    config.withCredentials = false;
    return config;
  }

  if (isCustomerEndpoint) {
    // Customer token for my-orders, checkout, addresses, etc.
    try {
      const slugFromUrl = getRestaurantSlugFromRequestUrl(config.url);
      const currentRestaurantSlug = slugFromUrl || getRestaurantSlugFromPathname();
      const { data } = readCustomerAuth(currentRestaurantSlug);
      let accessToken = data?.customerToken || data?.token || "";
      const refreshToken = data?.customerRefreshToken || data?.refreshToken || "";

      if (accessToken && isTokenExpired(accessToken)) {
        const refreshed = await tryRefreshCustomerToken(currentRestaurantSlug);
        if (refreshed?.customerToken) {
          accessToken = refreshed.customerToken;
        } else if (refreshToken || accessToken) {
          forceCustomerLogout(currentRestaurantSlug);
          if (isGuestAllowedCustomerEndpoint(config.url)) {
            return config;
          }
          return Promise.reject(new Error("Customer session expired"));
        }
      }

      if (accessToken && !isTokenExpired(accessToken)) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (e) {
      // ignore
    }
    return config;
  }

  // Get token from localStorage directly to avoid circular dependencies (BackOffice)
  const userData = localStorage.getItem("user");
  let token = null;

  if (userData) {
    try {
      const user = JSON.parse(userData);
      token = user.token;
    } catch (error) {
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
    }
  }

  if (token && !isTokenExpired(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (token && isTokenExpired(token)) {
    // Token is expired, clear it from localStorage
    localStorage.removeItem("user");
    localStorage.removeItem("refreshToken");
    // Note: We don't redirect here to avoid conflicts with routing
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;
    const isCustomerEndpoint = isOnlineOrderingCustomerEndpoint(originalRequest?.url);
    if (response.status === 401 && isCustomerEndpoint && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const slugFromUrl = getRestaurantSlugFromRequestUrl(originalRequest?.url);
      const currentRestaurantSlug = slugFromUrl || getRestaurantSlugFromPathname();
      const refreshed = await tryRefreshCustomerToken(currentRestaurantSlug);
      if (refreshed?.customerToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.customerToken}`;
        return axiosInstance(originalRequest);
      }
      forceCustomerLogout(currentRestaurantSlug);
      if (isGuestAllowedCustomerEndpoint(originalRequest?.url)) {
        if (originalRequest.headers?.Authorization) {
          delete originalRequest.headers.Authorization;
        }
        return axiosInstance(originalRequest);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isCustomerEndpoint = isOnlineOrderingCustomerEndpoint(originalRequest?.url);
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isCustomerEndpoint) {
        originalRequest._retry = true;
        const slugFromUrl = getRestaurantSlugFromRequestUrl(originalRequest?.url);
        const currentRestaurantSlug = slugFromUrl || getRestaurantSlugFromPathname();
        const refreshed = await tryRefreshCustomerToken(currentRestaurantSlug);
        if (refreshed?.customerToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${refreshed.customerToken}`;
          return axiosInstance(originalRequest);
        }
        forceCustomerLogout(currentRestaurantSlug);
        if (isGuestAllowedCustomerEndpoint(originalRequest?.url)) {
          if (originalRequest.headers?.Authorization) {
            delete originalRequest.headers.Authorization;
          }
          return axiosInstance(originalRequest);
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      
      try {
        // Check if keepMeLogin is enabled
        const userData = localStorage.getItem("user");
        const keepMeLoginStored = localStorage.getItem("keepMeLogin");
        let keepMeLogin = false;
        
        if (userData) {
          try {
            const user = JSON.parse(userData);
            keepMeLogin = user.keepMeLogin || false;
          } catch (error) {
            // Fallback to stored keepMeLogin preference
          }
        }
        
        // Also check the separate keepMeLogin storage
        if (!keepMeLogin && keepMeLoginStored) {
          try {
            keepMeLogin = JSON.parse(keepMeLoginStored);
          } catch (error) {
            // Invalid keepMeLogin data
          }
        }
        
        // Only attempt token refresh if keepMeLogin is enabled
        if (keepMeLogin) {
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken) {
            try {
              const refreshResponse = await axios.post(
                `${API_URL}/api/Auth/RefreshToken`,
                {
                  refreshToken,
                  clientType: "mobile"
                },
                { withCredentials: true }
              );
              
              if (refreshResponse.data.status && (refreshResponse.data.data.token || refreshResponse.data.data.refreshToken)) {
                // Update stored tokens in localStorage
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                if (refreshResponse.data.data.token) {
                  userData.token = refreshResponse.data.data.token;
                }
                userData.keepMeLogin = keepMeLogin; // Ensure keepMeLogin is preserved
                if (refreshResponse.data.data.refreshToken) {
                  userData.refreshToken = refreshResponse.data.data.refreshToken;
                  localStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
                }
                localStorage.setItem('user', JSON.stringify(userData));
                
                // Dispatch Redux action to update store
                window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
                  detail: { 
                    token: refreshResponse.data.data.token,
                    refreshToken: refreshResponse.data.data.refreshToken || refreshToken,
                    keepMeLogin: keepMeLogin
                  } 
                }));
                
                // Retry the original request with new token
                if (refreshResponse.data.data.token) {
                  originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.data.token}`;
                }
                return axiosInstance(originalRequest);
              } else {
                console.warn('Token refresh failed: Invalid response', refreshResponse.data);
              }
            } catch (refreshError) {
              console.warn('Token refresh request failed:', refreshError.response?.data?.message || refreshError.message);
              throw refreshError; // Re-throw to be caught by outer catch
            }
          } else {
            console.warn('No refresh token available for token refresh');
          }
        } else {
          console.info('keepMeLogin is disabled, not attempting token refresh');
        }
      } catch (refreshError) {
        console.error('Token refresh process failed:', refreshError);
      }
      
      // If refresh failed or keepMeLogin is disabled, clear tokens
      console.info('Clearing tokens and forcing logout due to 401 error');
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("keepMeLogin");
      
      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('forceLogout'));
      
      return Promise.reject(error);
    }
    
    // Handle other errors
    if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
